










































































































































































import { defineComponent, ref, watch, computed } from '@vue/composition-api';
import sl from '@/serviceLocator';

type CacheRouteType =
  | 'Collections'
  | 'Text Epigraphies'
  | 'Words'
  | 'Names'
  | 'Places'
  | 'Signs'
  | 'Properties Taxonomy Tree'
  | 'Dictionary'
  | 'Page Content'
  | 'Bibliography'
  | 'Seal'
  | 'Persons'
  | 'Periods';

interface RoutePiece {
  type: 'static' | 'param' | 'separator';
  label: string;
  value?: string;
}

interface IndividualClearTemplate {
  name: string;
  routePieces: RoutePiece[];
  level: 'exact' | 'startsWith';
  type: CacheRouteType;
}

export default defineComponent({
  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },
  setup(_props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const _ = sl.get('lodash');

    const individualClearLoading = ref(false);
    const routeToClear = ref('/');

    const selectedLevel = ref<'exact' | 'startsWith'>('exact');
    const clearRoute = async () => {
      try {
        individualClearLoading.value = true;
        await server.clearCacheRoute(routeToClear.value, selectedLevel.value);
        actions.showSnackbar('Successfully cleared specified cache route(s).');
      } catch (err) {
        actions.showErrorSnackbar(
          'Error clearing individual cache route(s). Please try again.',
          err as Error
        );
      } finally {
        individualClearLoading.value = false;
        emit('input', false);
        routeToClear.value = '/';
        selectedLevel.value = 'exact';
      }
    };

    const numMatchingKeys = ref(0);
    const getMatchingKeys = async () => {
      try {
        numMatchingKeys.value = await server.getNumKeys(
          routeToClear.value,
          selectedLevel.value
        );
      } catch (err) {
        actions.showErrorSnackbar(
          'Error getting matching keys. Please try again.',
          err as Error
        );
      }
    };

    watch([routeToClear, selectedLevel], _.debounce(getMatchingKeys, 200));

    const allCacheRouteTypes = ref<CacheRouteType[]>([
      'Collections',
      'Text Epigraphies',
      'Words',
      'Names',
      'Places',
      'Signs',
      'Properties Taxonomy Tree',
      'Dictionary',
      'Page Content',
      'Bibliography',
      'Seal',
      'Persons',
      'Periods',
    ]);

    const individualClearTemplates = ref<IndividualClearTemplate[]>([
      {
        name: 'Collections List',
        routePieces: [
          { type: 'separator', label: '/' },
          { type: 'static', label: 'collections' },
        ],
        level: 'exact',
        type: 'Collections',
      },
      {
        name: 'Collection Texts - All',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'collections',
          },
          { type: 'separator', label: '/' },
        ],
        level: 'startsWith',
        type: 'Collections',
      },
      {
        name: 'Collection Texts - Specific',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'collections',
          },
          { type: 'separator', label: '/' },
          {
            type: 'param',
            label: 'Collection UUID',
          },
        ],
        level: 'exact',
        type: 'Collections',
      },
      {
        name: 'Dictionary Words - All',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'dictionary',
          },
          { type: 'separator', label: '/' },
        ],
        level: 'startsWith',
        type: 'Dictionary',
      },
      {
        name: 'Dictionary Word - Specific',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'dictionary',
          },
          { type: 'separator', label: '/' },
          {
            type: 'param',
            label: 'Word UUID',
          },
        ],
        level: 'exact',
        type: 'Dictionary',
      },
      {
        name: 'Properties Taxonomy Tree',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'properties_taxonomy_tree',
          },
        ],
        level: 'exact',
        type: 'Properties Taxonomy Tree',
      },
      {
        name: 'Words Pages - All',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'words',
          },
          { type: 'separator', label: '/' },
        ],
        level: 'startsWith',
        type: 'Words',
      },
      {
        name: 'Words Page - Specific',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'words',
          },
          { type: 'separator', label: '/' },
          {
            type: 'param',
            label: 'Letter',
          },
        ],
        level: 'exact',
        type: 'Words',
      },
      {
        name: 'Names Pages - All',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'names',
          },
          { type: 'separator', label: '/' },
        ],
        level: 'startsWith',
        type: 'Names',
      },
      {
        name: 'Names Page - Specific',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'names',
          },
          { type: 'separator', label: '/' },
          {
            type: 'param',
            label: 'Letter',
          },
        ],
        level: 'exact',
        type: 'Names',
      },
      {
        name: 'Places Pages - All',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'places',
          },
          { type: 'separator', label: '/' },
        ],
        level: 'startsWith',
        type: 'Places',
      },
      {
        name: 'Places Page - Specific',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'places',
          },
          { type: 'separator', label: '/' },
          {
            type: 'param',
            label: 'Letter',
          },
        ],
        level: 'exact',
        type: 'Places',
      },
      {
        name: 'Page Content',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'page_content',
          },
          { type: 'separator', label: '/' },
          {
            type: 'param',
            label: 'Route Name',
          },
        ],
        level: 'exact',
        type: 'Page Content',
      },
      {
        name: 'Sign List',
        type: 'Signs',
        level: 'startsWith',
        routePieces: [
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'static',
            label: 'signList',
          },
        ],
      },
      {
        name: 'Sign Readings',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'sign_reading',
          },
        ],
        level: 'startsWith',
        type: 'Signs',
      },
      {
        name: 'Text Epigraphies - All',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'text_epigraphies',
          },
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'text',
          },
          { type: 'separator', label: '/' },
        ],
        level: 'startsWith',
        type: 'Text Epigraphies',
      },
      {
        name: 'Text Epigraphies - Specfic',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'text_epigraphies',
          },
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'text',
          },
          { type: 'separator', label: '/' },
          {
            type: 'param',
            label: 'Text UUID',
          },
        ],
        level: 'startsWith',
        type: 'Text Epigraphies',
      },
      {
        name: 'Text Source Files - All',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'text_epigraphies',
          },
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'text_source',
          },
          { type: 'separator', label: '/' },
        ],
        level: 'startsWith',
        type: 'Text Epigraphies',
      },
      {
        name: 'Text Source Files - Specific',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'text_epigraphies',
          },
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'text_source',
          },
          { type: 'separator', label: '/' },
          {
            type: 'param',
            label: 'Text UUID',
          },
        ],
        level: 'startsWith',
        type: 'Text Epigraphies',
      },
      {
        name: 'Bibliographies List',
        level: 'startsWith',
        type: 'Bibliography',
        routePieces: [
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'static',
            label: 'bibliographies',
          },
        ],
      },
      {
        name: 'Bibliographies - All',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'bibliography',
          },
          {
            type: 'separator',
            label: '/',
          },
        ],
        level: 'startsWith',
        type: 'Bibliography',
      },
      {
        name: 'Bibliography - Specific',
        routePieces: [
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'static',
            label: 'bibliography',
          },
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'param',
            label: 'Bibliography UUID',
          },
        ],
        level: 'exact',
        type: 'Bibliography',
      },
      {
        name: 'Seals - All',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'seals',
          },
          { type: 'separator', label: '/' },
        ],
        level: 'startsWith',
        type: 'Seal',
      },
      {
        name: 'Seals - Specific',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'seals',
          },
          { type: 'separator', label: '/' },
          {
            type: 'param',
            label: 'Seal UUID',
          },
        ],
        level: 'exact',
        type: 'Seal',
      },
      {
        name: 'Seals List',
        routePieces: [
          { type: 'separator', label: '/' },
          {
            type: 'static',
            label: 'seals',
          },
        ],
        level: 'exact',
        type: 'Seal',
      },
      {
        name: 'Persons List - All',
        routePieces: [
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'static',
            label: 'persons',
          },
          {
            type: 'separator',
            label: '/',
          },
        ],
        level: 'startsWith',
        type: 'Persons',
      },
      {
        name: 'Persons List - Specific',
        routePieces: [
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'static',
            label: 'persons',
          },
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'param',
            label: 'Letter',
          },
        ],
        level: 'exact',
        type: 'Persons',
      },
      {
        name: 'Individual Persons - All',
        type: 'Persons',
        level: 'startsWith',
        routePieces: [
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'static',
            label: 'person',
          },
          {
            type: 'separator',
            label: '/',
          },
        ],
      },
      {
        name: 'Individual Person - Specific',
        type: 'Persons',
        level: 'exact',
        routePieces: [
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'static',
            label: 'person',
          },
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'param',
            label: 'Person UUID',
          },
        ],
      },
      {
        name: 'Periods List',
        level: 'exact',
        type: 'Periods',
        routePieces: [
          {
            type: 'separator',
            label: '/',
          },
          {
            type: 'static',
            label: 'periods',
          },
        ],
      },
    ]);

    const selectedTemplate = ref<IndividualClearTemplate>();

    const modeSelection = ref<'template' | 'manual'>();

    watch(
      selectedTemplate,
      () => {
        if (selectedTemplate.value) {
          selectedLevel.value = selectedTemplate.value.level;

          const mergedRoutePieces: string[] = [];
          selectedTemplate.value.routePieces.forEach(piece => {
            if (piece.type === 'separator' || piece.type === 'static') {
              mergedRoutePieces.push(piece.label);
            } else {
              mergedRoutePieces.push(piece.value || '');
            }
          });

          routeToClear.value = mergedRoutePieces.join('');
        } else {
          routeToClear.value = '/';
          selectedLevel.value = 'exact';
        }
      },
      { deep: true }
    );

    const handleBack = () => {
      selectedTemplate.value = undefined;
      routeToClear.value = '/';
      selectedLevel.value = 'exact';
      selectedTemplateType.value = undefined;
      modeSelection.value = undefined;
    };

    const title = computed(() => {
      switch (modeSelection.value) {
        case 'template':
          return 'Clear Cache - Templates';
        case 'manual':
          return 'Clear Cache - Manual';
        default:
          return 'Clear Individual Cache Entry';
      }
    });

    const selectedTemplateType = ref<CacheRouteType>();

    watch(selectedTemplateType, () => {
      selectedTemplate.value = undefined;
      routeToClear.value = '/';
      selectedLevel.value = 'exact';
    });

    return {
      clearRoute,
      individualClearLoading,
      routeToClear,
      selectedLevel,
      numMatchingKeys,
      individualClearTemplates,
      modeSelection,
      selectedTemplate,
      handleBack,
      allCacheRouteTypes,
      title,
      selectedTemplateType,
    };
  },
});
